import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useInvalidateMyFollowUps } from "@/hooks/useMyFollowUps";
import api, { API } from "@/lib/api";
import { workspaceBindingQuery } from "@/lib/workspaceBinding";
import { onReread } from "@/lib/reread";
import { toast } from "sonner";
import { UserCheck } from "lucide-react";

const RealtimeContext = createContext({ revision: 0, onlineIds: [], viewersByDecision: {}, setViewing: () => {} });

// Team Pulse Sync: a single SSE connection, scoped to the current workspace,
// that revalidates the shared follow-up cache when teammates make changes,
// and tracks which teammates are currently online (presence).
// It keeps no duplicate follow-up state — it only invalidates/refetches.
export function RealtimeProvider({ children }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const invalidate = useInvalidateMyFollowUps();
  const [revision, setRevision] = useState(0);
  const [onlineIds, setOnlineIds] = useState([]);
  const [viewersByDecision, setViewersByDecision] = useState({});
  const esRef = useRef(null);
  const [streamGeneration, setStreamGeneration] = useState(0);
  const viewingRef = useRef([]);

  const bump = useCallback(() => {
    invalidate();
    setRevision((r) => r + 1);
  }, [invalidate]);

  // Report which decisions the current user is viewing (from the Decision Board).
  const setViewing = useCallback((ids) => {
    const next = Array.isArray(ids) ? ids : [];
    viewingRef.current = next;
    api.post("/presence/heartbeat", { viewing: next }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!user) return undefined;
    setOnlineIds([]);
    setViewersByDecision({});
    // D10: an EventSource cannot send headers; the page's workspace travels in the URL. A reconnection after the
    // workspace changed elsewhere is refused (409), which ends the stream instead of joining the other workspace.
    const es = new EventSource(`${API}/events/stream${workspaceBindingQuery(user)}`, { withCredentials: true });
    esRef.current = es;
    // Revalidate whenever the stream (re)connects — covers reconnect after a drop.
    es.addEventListener("ready", () => bump());
    es.onmessage = (e) => {
      let msg = null;
      try { msg = JSON.parse(e.data); } catch { msg = null; }
      if (msg && msg.type === "presence") {
        setOnlineIds(Array.isArray(msg.online) ? msg.online : []);
        return;
      }
      if (msg && msg.type === "viewers") {
        setViewersByDecision(msg.viewers && typeof msg.viewers === "object" ? msg.viewers : {});
        return;
      }
      // A teammate just assigned a follow-up to me -> subtle heads-up toast.
      if (
        msg && msg.type === "follow_ups_changed" &&
        msg.event === "decision.assigned" &&
        msg.assignee_id === user.id &&
        msg.actor_id !== user.id
      ) {
        const target = msg.comment_id
          ? `/decisions?assignee=me&focus=${msg.comment_id}`
          : "/decisions?assignee=me";
        toast(`${msg.actor_name || "A teammate"} assigned you a follow-up`, {
          description: "Tap to jump to it on the Decision Board.",
          icon: <UserCheck className="h-4 w-4" />,
          action: { label: "View", onClick: () => navigate(target) },
        });
      }
      // Any workspace follow-up event -> revalidate shared cache + dependent views.
      bump();
    };
    // On error the browser auto-reconnects; the next 'ready' will trigger a revalidate.
    es.onerror = () => {};
    return () => {
      es.close();
      esRef.current = null;
    };
    // Reconnect when the identity or active workspace changes, or when a stream ended by a refusal may resume.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, user?.workspace_id, streamGeneration]);

  // D10: once this page's workspace is current again, a stream that the server refused is opened again.
  useEffect(() => onReread(() => { if (esRef.current && esRef.current.readyState === 2) setStreamGeneration((g) => g + 1); }), []);

  // Also revalidate when the tab regains focus or the network comes back.
  useEffect(() => {
    if (!user) return undefined;
    const onWake = () => bump();
    window.addEventListener("focus", onWake);
    window.addEventListener("online", onWake);
    return () => {
      window.removeEventListener("focus", onWake);
      window.removeEventListener("online", onWake);
    };
  }, [user, bump]);

  // Presence heartbeat: keeps this user marked online (robust behind the proxy),
  // and a tab-close beacon marks them offline promptly.
  useEffect(() => {
    if (!user) return undefined;
    let cancelled = false;
    const beat = () => { api.post("/presence/heartbeat", { viewing: viewingRef.current }).catch(() => {}); };
    beat();
    const id = setInterval(() => { if (!cancelled) beat(); }, 10000);
    const goOffline = () => {
      try { navigator.sendBeacon(`${API}/presence/offline${workspaceBindingQuery(user)}`); } catch { /* ignore */ }
    };
    window.addEventListener("pagehide", goOffline);
    return () => {
      cancelled = true;
      clearInterval(id);
      window.removeEventListener("pagehide", goOffline);
      goOffline();
    };
  }, [user?.id, user?.workspace_id]); // eslint-disable-line react-hooks/exhaustive-deps

  return <RealtimeContext.Provider value={{ revision, onlineIds, viewersByDecision, setViewing }}>{children}</RealtimeContext.Provider>;
}

export function useRealtime() {
  return useContext(RealtimeContext);
}
